/**
 * @name InterceptorHTTP
 * @description Interceptor Axios HTTP Middleware
 * @author Ricardo Guerra
 */

import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import moment from 'moment';
import { Trans } from 'react-i18next';
import { Service } from 'axios-middleware';

import { Environment } from '@config';
import { LoadingTopBar } from '@components';
import { AuthService, SnackbarService } from '@services';

const InterceptorHTTP = new Service(axios);

// Request List
let requestQueue = [];
let isFirstEntry = false; // Flag to know is Login first time
let isRefreshingToken = false; // Flag to know is refreshing token

/**
 * NOTA:
 * (1) Si el token está vencido pero el refresh token está dentro del rango, pedir autenticación con refresh token.
 * (2) Si el token está vencido y el refresh token también está vencido y hay credenciales (keepSession), entonces pedir autenticación con credenciales.
 * (3) Si el token está vencido y el refresh token también está vencido, y no hay credenciales, entonces Logout. 
 */

// Axios Middleware API
InterceptorHTTP.register({
  onRequest(config) {
    return requestHandler(config);
  },
  onRequestError(error) {
    return Promise.reject(error);
  },
  onSync(promise) {
    return promise;
  },
  onResponse(response) {
    return successHandler(response);
  },
  onResponseError(error) {
    return errorHandler(error);
  }
});

// Resolve Process RequestQueue
const processQueue = (error, accessToken) => {
  requestQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(accessToken);
    }
  });

  requestQueue = [];
};

/**
 * Ejecución de cada SUCCESS de response HTTP
 * @param {any} response Response HTTP Axios
 * @return {any} response
 */
const successHandler = (response) => {
  ReactDOM.render(
    <LoadingTopBar />,
    document.getElementById('loading-bar')
  ).complete();

  return response;
};

/**
 * Ejecución de cada ERROR de response HTTP
 * @param {any} error Error HTTP Axios
 * @return {any} config
 */
const errorHandler = (error) => {
  ReactDOM.render(
    <LoadingTopBar />,
    document.getElementById('loading-bar')
  ).complete();

  const request = error.config;

  // ERROR 401 (Unauthorized) Token
  if (error.response && error.response.status === 401 && !request._isRetryRequest) {
    // Si hay request para obtener el nuevo token, guardar todos los request
    if (isRefreshingToken) {
      return new Promise((resolve, reject) => {
        requestQueue.push({ resolve, reject });
      }).then((accessToken) => {
        request.headers['Authorization'] = `Bearer ${accessToken}`;
        return axios(request);
      }).catch((error) => {
        return Promise.reject(error);
      });
    }

    request._isRetryRequest = true;

    const expirationDate = JSON.parse(localStorage.getItem('ls.authorizationData')).expirationDate;
    const isTokenExpired = !(expirationDate && (new Date() < new Date(expirationDate)));

    // Si el token está expirado, realizar autenticación con refreshToken
    if (isTokenExpired) {
      isRefreshingToken = true;

      const currentEnvironment = Environment.getCurrent();
      const refreshToken = JSON.parse(localStorage.getItem('ls.authorizationData')).refreshToken;
      const language = JSON.parse(localStorage.getItem('ls.ty-Language')).language;

      const authData = `grant_type=refresh_token&refresh_token=${refreshToken}&client_id=${currentEnvironment.clientId}`;

      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept-Language': language
      };

      // Autenticación con refreshToken
      return new Promise((resolve, reject) => {
        axios.post(currentEnvironment.endpoints.auth, authData, { headers })
          .then(({ data }) => {
            // Guardar información en LocalStorage
            setAuthData(data);
            // Ejecutar lista de request pendientes con el nuevo token
            processQueue(null, data.access_token);
            // Ejecutar request original con el nuevo token
            request.headers['Authorization'] = `Bearer ${data.access_token}`;
            resolve(axios(request));
          })
          .catch((error) => {
            // Si el refreshToken ya caducó
            const credentials = JSON.parse(localStorage.getItem('ls.credentials'));
            const clientId = Environment.getCurrent().clientId;

            // Intentar autenticación si se tienen credenciales (keepSession)
            if (credentials) {
              let credentialsData = `grant_type=password&username=${encodeURIComponent(credentials.userName)}&password=${encodeURIComponent(credentials.password)}&client_id=${clientId}`;

              // Autenticación con credenciales
              return new Promise((resolve, reject) => {
                axios.post(currentEnvironment.endpoints.auth, credentialsData, { headers })
                  .then(({ data }) => {
                    // Guardar información en LocalStorage
                    setAuthData(data);
                    // Ejecutar lista de request pendientes con el nuevo token
                    processQueue(null, data.access_token);
                    // Ejecutar request original con el nuevo token
                    request.headers['Authorization'] = `Bearer ${data.access_token}`;
                    resolve(axios(request));
                  })
                  .catch(() => {
                    // Error en autenticación con credenciales, Logout
                    SnackbarService.error(<Trans>Login.sessionExpired</Trans>);
                    setTimeout(() => {
                      processQueue(error, null);
                      AuthService.clearStorage();
                      AuthService.logout();
                      return reject(error);
                    }, 1000);
                  })
                  .then(() => {
                    isRefreshingToken = false;
                  });
              });
            } else {
              // Si no se tienen credenciales, Logout
              SnackbarService.error(<Trans>Login.sessionExpired</Trans>);
              setTimeout(() => {
                processQueue(error, null);
                AuthService.clearStorage();
                AuthService.logout();
                return reject(error);
              }, 1000);
            }
          }).then(() => {
            isRefreshingToken = false;
          });
      });
    } else {
      // Si el token no está expirado, ejecutar request con el nuevo token
      return axios(request);
    }
  } else if (!error.response) {
    // SnackbarService.error(<Trans>GenericMessages.errorContactAdmin</Trans>);
    // error.response = { status: 'Network Error' };
    // Promise.reject(error);
    return Promise.reject(null);
  }

  return Promise.reject(error);
};

/**
 * Ejecución de cada request HTTP
 * @param {any} config Request HTTP Axios
 * @return {any} config
 */
const requestHandler = (config) => {
  ReactDOM.render(
    <LoadingTopBar />,
    document.getElementById('loading-bar')
  ).start();

  const currentEnvironment = Environment.getCurrent();

  const isHTMLRequest = ((URL) => {
    let string = URL.split('.');
    return string[string.length - 1] === 'html';
  })(config.url);

  // Si es Login por primera vez y viene de correo, el URL será guardado para después redireccionar
  if (isFirstEntry === false) {
    if (window.location.href.substr(window.location.href.length - 2) === '?1') {
      localStorage.setItem('URLToRedirect', window.location.href.slice(0, -2).split('#')[1].substr(1));
    }
    isFirstEntry = true;
  }

  // Si el request config ya tiene atributo 'Authorization', retornar config original
  if (typeof config.headers.Authorization !== 'undefined') {
    return config;
  }

  // Si es request HTML ó de Token, retornar config original
  if (config.url === currentEnvironment.endpoints.auth || isHTMLRequest) {
    return config;
  }

  const authData = JSON.parse(localStorage.getItem('ls.authorizationData'));
  const isTokenAlive = authData && authData.expirationDate && (new Date() < new Date(authData.expirationDate));

  // Si el token aún está vivo
  if (authData && authData.token && isTokenAlive) {
    return setHeaders(config);
  }
  else {
    // Si el token ya expiró
    return config;
  }
};

/**
 * Añade headers a todos los request HTTP (Token y Lenguaje)
 * @param {object} config Request HTTP Axios
 * @return {any} config
 */
const setHeaders = (config) => {
  config.headers = config.headers || {};

  const token = JSON.parse(localStorage.getItem('ls.authorizationData')).token;
  const language = JSON.parse(localStorage.getItem('ls.ty-Language')).language;

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  if (language) {
    config.headers['Accept-Language'] = language;
  }

  return config;
};

/**
 * Guardar y actualizar información de autenticación en LocalStorage
 * @param {object} data Authentication Data
 */
const setAuthData = (data) => {
  localStorage.setItem('ls.authorizationData', JSON.stringify({
    token: data.access_token,
    refreshToken: data.refresh_token,
    userName: data.userName,
    useRefreshTokens: true,
    expirationDate: moment().add(data.expires_in, 'seconds')
  }));
};

export default InterceptorHTTP;